
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import hotelImg from "../assests/images/hotel-bg.jpg";

import {
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Clock3,
  Compass,
  Headphones,
  Mail,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const PHONE = "+971 4 447 3839";
const EMAIL = "info@southtravels.com";
const ADDRESS =
  "Office 302, 3rd Flr. HDS Tower, Cluster F, Jumeirah Lakes Towers, Dubai";

const MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=HDS+Tower+Cluster+F+Jumeirah+Lakes+Towers+Dubai";

const MAP_EMBED =
  "https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d3613.775341638421!2d55.143142861862316!3d25.0756025365207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sOffice%20302%2C%203rd%20Flr.%20HDS%20Tower%20Cluster%20F%2C%20Jumeirah%20Lakes%20Towers%20Dubai!5e0!3m2!1sen!2sae!4v1789480084690!5m2!1sen!2sae";

const contactCards = [
  {
    icon: Phone,
    label: "Call Our Team",
    title: PHONE,
    description: "Speak with our Dubai travel team.",
    href: `tel:${PHONE.replace(/[^+\d]/g, "")}`,
    action: "Call Now",
  },
  {
    icon: Mail,
    label: "Email Us",
    title: EMAIL,
    description: "Send us your questions or enquiries.",
    href: `mailto:${EMAIL}`,
    action: "Send Email",
  },
  {
    icon: MapPin,
    label: "Visit Our Office",
    title: "Jumeirah Lakes Towers",
    description: "HDS Tower, Cluster F, Dubai.",
    href: MAP_URL,
    action: "Get Directions",
    external: true,
  },
];

export default function ContactUs() {
  return (
    <>
      <Helmet>
        <title>Contact Us | Dubai Budgetive Hotels</title>
        <meta
          name="description"
          content="Contact our Dubai travel team for hotel bookings, travel services, tours, visa assistance, car rentals and booking support."
        />
        <link
          rel="canonical"
          href="https://dubaibudgetivehotels.com/contact"
        />
        <meta
          property="og:title"
          content="Contact Us | Dubai Budgetive Hotels"
        />
        <meta
          property="og:description"
          content="Our Dubai travel team is here to help you plan your next trip."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="min-h-screen overflow-hidden bg-white text-[#17324D]">

        {/* ================================================
            FULL WIDTH CONTACT BANNER
        ================================================= */}

        <section className="relative isolate flex min-h-[440px] items-center overflow-hidden bg-[#064E3B] pt-24 sm:min-h-[500px]">
          <img
            src={hotelImg}
            alt="Dubai hotels and travel"
            className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
          />

          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/85 to-[#064E3B]/55" />

          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#032F28]/40 to-transparent" />

          <div className="mx-auto w-full max-w-[1536px] px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-[790px]">

              <nav
                aria-label="Breadcrumb"
                className="mb-8 flex items-center gap-2 text-xs font-semibold text-white/75"
              >
                <Link to="/" className="hover:text-white">
                  Home
                </Link>

                <ChevronRight size={14} />

                <span className="text-[#B6F2CB]">
                  Contact Us
                </span>
              </nav>

              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB] backdrop-blur-sm">
                <MessageCircle size={15} />
                We're Here To Help
              </div>

              <h1 className="text-[43px] font-extrabold leading-[1.12] tracking-[-0.045em] text-white sm:text-[58px] lg:text-[68px]">
                Let's Start A
                <br />
                <span className="text-[#A5E8B8]">
                  Conversation.
                </span>
              </h1>

              <p className="mt-6 max-w-[630px] text-[15px] leading-8 text-white/80 sm:text-base">
                Have questions about Dubai hotels, reservations,
                or your travel plans? Our team is ready to help
                you make your journey easier.
              </p>

              <a
                href="#contact-form"
                className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#087D37]"
              >
                Send Us A Message
                <ArrowRight size={17} />
              </a>
            </div>
          </div>
        </section>

        {/* ================================================
            CONTACT INFORMATION CARDS
        ================================================= */}

        <section className="relative bg-[#F5FAF7] py-16 sm:py-20">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

            <div className="mb-10 text-center">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                Get In Touch
              </p>

              <h2 className="mt-3 text-[32px] font-extrabold tracking-tight text-[#17324D] sm:text-[43px]">
                We're Just A{" "}
                <span className="text-[#168B43]">
                  Message Away.
                </span>
              </h2>

              <p className="mx-auto mt-4 max-w-[620px] text-sm leading-8 text-[#718096]">
                Choose the easiest way to connect with our team.
                We're happy to assist with your travel enquiries.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {contactCards.map((card) => {
                const Icon = card.icon;

                return (
                  <a
                    key={card.label}
                    href={card.href}
                    target={card.external ? "_blank" : undefined}
                    rel={
                      card.external
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="group relative flex h-full flex-col overflow-hidden rounded-[20px] border border-[#E0ECE4] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#A9DDB9] hover:shadow-[0_18px_45px_rgba(6,78,59,0.09)] sm:p-8"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43] transition group-hover:bg-[#168B43] group-hover:text-white">
                        <Icon size={25} strokeWidth={1.8} />
                      </div>

                      <ArrowUpRight
                        size={20}
                        className="text-[#A1B1A8] transition group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#168B43]"
                      />
                    </div>

                    <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                      {card.label}
                    </p>

                    <h3 className="mt-3 break-words text-[20px] font-extrabold leading-snug text-[#17324D] sm:text-[22px]">
                      {card.title}
                    </h3>

                    <p className="mt-3 text-[13px] leading-7 text-[#718096]">
                      {card.description}
                    </p>

                    <div className="mt-auto flex items-center gap-2 pt-7 text-xs font-extrabold text-[#168B43]">
                      {card.action}
                      <ArrowRight
                        size={16}
                        className="transition group-hover:translate-x-1"
                      />
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* ================================================
            ENQUIRY FORM SECTION
        ================================================= */}

        <section
          id="contact-form"
          className="scroll-mt-24 bg-white py-16 sm:py-24"
        >
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

            <div className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[700px]">
                <div className="mb-4 flex items-center gap-3">
                  <span className="h-[2px] w-8 bg-[#168B43]" />

                  <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                    Send Your Enquiry
                  </p>
                </div>

                <h2 className="text-[34px] font-extrabold leading-tight tracking-tight text-[#17324D] sm:text-[46px]">
                  How Can We{" "}
                  <span className="text-[#168B43]">
                    Help You?
                  </span>
                </h2>
              </div>

              <p className="max-w-[450px] text-sm leading-8 text-[#718096]">
                Whether you need hotel recommendations, booking
                support, or general travel assistance, send us
                your enquiry using the form below.
              </p>
            </div>

            <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1.5fr)_minmax(300px,0.75fr)] lg:gap-10">

              {/* FORM COMPONENT */}
              <div className="min-w-0">
                <ContactForm />
              </div>

              {/* RIGHT SIDEBAR */}
              <aside className="space-y-6">

                <div className="relative isolate overflow-hidden rounded-[22px] bg-[#064E3B] p-7 text-white sm:p-9">
                  <div className="pointer-events-none absolute -right-20 -top-20 -z-10 h-60 w-60 rounded-full border border-white/10" />
                  <div className="pointer-events-none absolute -right-10 -top-10 -z-10 h-40 w-40 rounded-full border border-white/10" />

                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-[#B6F2CB]">
                    <Headphones size={27} />
                  </div>

                  <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#B6F2CB]">
                    Personal Travel Assistance
                  </p>

                  <h3 className="mt-4 text-[29px] font-extrabold leading-tight text-white">
                    We're Here For Your Next Adventure.
                  </h3>

                  <p className="mt-4 text-sm leading-8 text-white/75">
                    Our Dubai team can help with hotel enquiries,
                    travel services, and questions about your
                    existing bookings.
                  </p>

                  <div className="mt-8 border-t border-white/20 pt-7">
                    <p className="text-xs text-white/60">
                      Prefer to speak with someone?
                    </p>

                    <a
                      href={`tel:${PHONE.replace(/[^+\d]/g, "")}`}
                      className="mt-3 inline-flex items-center gap-3 text-lg font-extrabold text-white transition hover:text-[#B6F2CB]"
                    >
                      <Phone size={19} />
                      {PHONE}
                    </a>
                  </div>
                </div>

                <div className="rounded-[20px] border border-[#DCEAE1] bg-[#F5FAF7] p-7">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#E2F4E9] text-[#168B43]">
                      <ShieldCheck size={24} />
                    </div>

                    <div>
                      <h3 className="text-[17px] font-extrabold text-[#17324D]">
                        Booking Assistance
                      </h3>

                      <p className="mt-2 text-[13px] leading-7 text-[#718096]">
                        Have an existing reservation? Include
                        your booking reference and details so
                        our team can assist you.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-[20px] border border-[#DCEAE1] bg-white p-7">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                      <Clock3 size={24} />
                    </div>

                    <div>
                      <h3 className="text-[17px] font-extrabold text-[#17324D]">
                        Message Us Anytime
                      </h3>

                      <p className="mt-2 text-[13px] leading-7 text-[#718096]">
                        Submit your enquiry whenever convenient.
                        Our team will respond as soon as possible.
                      </p>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* ================================================
            OFFICE LOCATION
        ================================================= */}

        <section className="bg-[#F5FAF7] py-16 sm:py-24">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">

            <div className="mb-10 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                  Find Us In Dubai
                </p>

                <h2 className="mt-4 text-[34px] font-extrabold tracking-tight text-[#17324D] sm:text-[46px]">
                  Visit Our{" "}
                  <span className="text-[#168B43]">
                    Office.
                  </span>
                </h2>
              </div>

              <p className="max-w-[430px] text-sm leading-8 text-[#718096]">
                Located in Jumeirah Lakes Towers, Dubai.
                Use the map below to find directions to our office.
              </p>
            </div>

            <div className="grid overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white shadow-[0_15px_45px_rgba(6,78,59,0.05)] lg:grid-cols-[1.4fr_0.8fr]">

              {/* GOOGLE MAP */}
              <div className="min-h-[360px] bg-[#E8F0EB] sm:min-h-[450px]">
                <iframe
                  title="Office location in Jumeirah Lakes Towers, Dubai"
                  src={MAP_EMBED}
                  width="100%"
                  height="100%"
                  style={{
                    border: 0,
                    minHeight: "360px",
                  }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>

              {/* ADDRESS PANEL */}
              <div className="flex flex-col justify-center p-7 sm:p-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                  <MapPin size={27} />
                </div>

                <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                  Our Location
                </p>

                <h3 className="mt-3 text-[27px] font-extrabold leading-tight text-[#17324D]">
                  HDS Tower,
                  <br />
                  Cluster F
                </h3>

                <p className="mt-5 text-sm leading-8 text-[#718096]">
                  {ADDRESS}
                </p>

                <a
                  href={MAP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex w-fit items-center gap-3 rounded-xl bg-[#168B43] px-6 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                >
                  <Navigation size={17} />
                  Get Directions
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
